import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

const QuoteForm = () => {
  const [submitted, setSubmitted] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!agreed) {
      toast.error("Please agree to be contacted about your request.");
      return;
    }

    setLoading(true);
    const formData = new FormData(e.currentTarget);

    const { data, error } = await supabase.functions.invoke("send-form-email", {
      body: {
        formType: "quote",
        name: formData.get("fullName"),
        email: formData.get("email"),
        phone: formData.get("phone"),
        giftType: formData.get("giftType"),
        timeframe: formData.get("timeframe"),
        description: formData.get("description"),
      },
    });

    setLoading(false);

    if (error) {
      toast.error("Something went wrong. Please try again.");
      console.error("Quote form error:", error);
      return;
    }

    setSubmitted(true);
  };

  if (submitted) {
    return (
      <div className="text-center py-16 animate-fade-in">
        <div className="w-16 h-16 rounded-full bg-secondary mx-auto flex items-center justify-center mb-4">
          <span className="text-2xl">✨</span>
        </div>
        <h3 className="text-2xl font-serif font-semibold mb-2">Thank you!</h3>
        <p className="text-muted-foreground">We'll reply within 24 hours.</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5 max-w-2xl mx-auto">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="fullName">Full Name *</Label>
          <Input id="fullName" name="fullName" required placeholder="Your name" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="email">Email *</Label>
          <Input id="email" name="email" type="email" required placeholder="your@email.com" />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="phone">Phone (optional)</Label>
        <Input id="phone" name="phone" type="tel" placeholder="+44 ..." />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>What's your gift idea? *</Label>
          <Select required name="giftType">
            <SelectTrigger>
              <SelectValue placeholder="Select type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="bottle">Bottle</SelectItem>
              <SelectItem value="painting">Painting</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Timeframe *</Label>
          <Select required name="timeframe">
            <SelectTrigger>
              <SelectValue placeholder="Select timeframe" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="3-5-days">3–5 days</SelectItem>
              <SelectItem value="1-2-weeks">1–2 weeks</SelectItem>
              <SelectItem value="2-4-weeks">2–4 weeks</SelectItem>
              <SelectItem value="not-sure">Not sure</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="description">Describe how you see the end product *</Label>
        <Textarea
          id="description"
          name="description"
          required
          rows={4}
          placeholder="Tell us about your vision..."
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="file">Upload reference image (optional)</Label>
        <Input id="file" name="file" type="file" accept="image/*" />
      </div>

      <div className="flex items-start gap-2">
        <Checkbox
          id="agree"
          checked={agreed}
          onCheckedChange={(checked) => setAgreed(checked === true)}
        />
        <Label htmlFor="agree" className="text-sm leading-relaxed cursor-pointer">
          I agree to be contacted about my request.
        </Label>
      </div>

      <Button type="submit" size="lg" className="w-full sm:w-auto" disabled={loading}>
        {loading ? "Sending..." : "Send Request"}
      </Button>
    </form>
  );
};

export default QuoteForm;
